import { NoUniqueInputIdContext } from "client/Contexts/UniqueInputIdContext";
import {
	convertArbitraryValueForTrackingId,
	generateTrackingId,
	isNotNil,
	isNotNilOrEmpty,
	setTrackingId
} from "client/Misc/utils";
import { ReactElement, useCallback } from "react";
import { Checkbox, CheckboxColor } from "../Checkbox/Checkbox";

export type CheckboxGroupItem<TValue> = {
	text: string;
	value: TValue;
	valueTrackingId?: string;
	isDisabled?: boolean;
	canChange?(newValue: boolean): Promise<boolean>;
};

export type CheckboxGroupProps<TValue> = {
	value: readonly TValue[];
	onChange(value: readonly TValue[]): void;
	options: readonly CheckboxGroupItem<TValue>[];
	trackingId?: string;
	color?: CheckboxColor;
};

export const CheckboxGroup = <TValue,>(props: CheckboxGroupProps<TValue>): ReactElement => {
	const {
		value,
		onChange,
		options,
		trackingId,
		color
	} = props;

	const changeHandler = async (item: CheckboxGroupItem<TValue>, becameChecked: boolean): Promise<void> => {
		let copy: TValue[];

		if (isNotNil(item.canChange)) {
			if (!(await item.canChange(becameChecked))) {
				return;
			}
		}

		if (becameChecked) {
			copy = [...value, item.value];
		} else {
			copy = value.filter(x => x !== item.value);
		}

		onChange(copy);
	};

	const getValueTrackingId = useCallback(
		(item: CheckboxGroupItem<TValue>): string => {
			if (isNotNilOrEmpty(item.valueTrackingId)) {
				return item.valueTrackingId;
			}

			return convertArbitraryValueForTrackingId(item.value);
		},
		[]
	);

	return (
		<div {...setTrackingId(trackingId)}>
			<NoUniqueInputIdContext>
				{options.map((item, index) => (
					<Checkbox
						key={index}
						value={value.includes(item.value)}
						text={item.text}
						onChange={(checkedState) => changeHandler(item, checkedState)}
						trackingId={generateTrackingId(trackingId, `|${getValueTrackingId(item)}`)}
						isDisabled={item.isDisabled}
						color={color}
					/>
				))}
			</NoUniqueInputIdContext>
		</div>
	);
};
CheckboxGroup.displayName = "CheckboxGroup";
